import html2canvas from "html2canvas";
import { PDFDocument } from "pdf-lib";
import { saveAs } from "file-saver";
import { utcToLocalDateTime } from "./rampChecklistTime";

/**
 * 把检查单渲染成 HTML 后截图，用 pdf-lib 拼成 A4 PDF 直接下载，不再经过浏览器打印对话框。
 *
 * 版式对齐桌面"过站检查单"标签页的 ChecklistSheet（纸质《Ramp Transit Checklist》/ OCM SECTION D - Annex 1）：
 * 抬头（左 OCM 标识 + 右 Fly Meta）+ Station + 表头两行五列网格（Flight Number / Aircraft REG / Date /
 * STA-ETA / ATA 与 RCO / Parking Spot / Crew Figure / STD-ETD / ATD）+ Tasks / Time / Check(√) 三列任务表
 * + 末尾 Remark / Signature 行。
 *
 * 走"隐藏 iframe 渲染 → html2canvas 截图 → pdf-lib 装页"而非 pdf-lib 直接排版：
 * 中文（备注、RCO / 停机位、签字人）按系统字体所见即所得，不必把 CJK 字体打进前端体积；
 * 签名以内嵌 PNG 图放进固定长方形区域；用户文本均 HTML 转义。代价是 PDF 内容为图片、文字不可选中。
 */

const escapeHtml = (value) =>
    String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

// A4 尺寸（pt）与页边距（10mm ≈ 28.35pt）；iframe 宽度取 A4 宽的 96dpi 像素值。
const A4_WIDTH_PT = 595.28;
const A4_HEIGHT_PT = 841.89;
const PAGE_MARGIN_PT = 28.35;
const RENDER_WIDTH_PX = 794;

function buildChecklistHtml({ checklist, tzId }) {
    const flight = checklist?.flight || {};
    const header = checklist?.header?.attributes || {};
    const items = checklist?.items || [];

    const hm = (utcIso) => (utcIso ? escapeHtml(utcToLocalDateTime(utcIso, tzId, "HH:mm")) : "");
    const dt = (utcIso) => (utcIso ? escapeHtml(utcToLocalDateTime(utcIso, tzId, "YYYY-MM-DD HH:mm")) : "");
    const itemTime = (utcIso) => (utcIso ? escapeHtml(utcToLocalDateTime(utcIso, tzId, "MM-DD HH:mm")) : "");
    const pair = (a, b) => [hm(a), hm(b)].filter(Boolean).join(" / ");

    // ATD / ATA 优先用检查单里手填的实际时刻，没填再回退航班主数据（FOP）。
    const ata = header.ata || flight.ata;
    const atd = header.atd || flight.atd;

    const rows = items
        .map((record) => {
            const a = record.attributes || {};
            return `
                <tr>
                    <td>${escapeHtml(a.label_en ?? "")}</td>
                    <td class="time">${itemTime(a.time)}</td>
                    <td class="chk">${a.checked ? "√" : ""}</td>
                </tr>`;
        })
        .join("");

    const signatureBlock = header.signature_data
        ? `<div class="sign-box"><img src="${escapeHtml(header.signature_data)}" alt="signature" /></div>`
        : `<div class="sign-box muted">未签名</div>`;

    const signer = `${escapeHtml(header.signed_by ?? "")}${header.signed_at ? ` · ${dt(header.signed_at)}` : ""}`;
    const completedLine = header.completed ? `<div class="done">已完成${header.completed_at ? ` · ${dt(header.completed_at)}` : ""}</div>` : "";

    // 下载文件名：空字段过滤掉避免尾部多余下划线。
    const title = ["RampChecklist", flight.flight_no, flight.flight_date].filter(Boolean).map(escapeHtml).join("_") || "RampChecklist";

    const html = `<!DOCTYPE html>
<html lang="zh-CN">
<head>
<meta charset="utf-8" />
<style>
    * { box-sizing: border-box; }
    body { font-family: "PingFang SC", "Microsoft YaHei", "Noto Sans CJK SC", system-ui, sans-serif; color: #000; margin: 0; padding: 8px; font-size: 11px; line-height: 1.4; background: #fff; }
    .topbar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; font-weight: 700; }
    .brand { color: #1677ff; }
    .title { text-align: center; font-size: 17px; font-weight: 700; margin-bottom: 4px; }
    .station { margin-bottom: 8px; }
    table { width: 100%; border-collapse: collapse; }
    td, th { border: 1px solid #333; padding: 3px 8px; vertical-align: top; }
    table.head { table-layout: fixed; margin-bottom: 10px; }
    table.head td { width: 20%; }
    table.head td.lbl { text-align: center; font-weight: 600; background: #fafafa; }
    table.head td.val { height: 22px; }
    table.tasks th { text-align: center; background: #fafafa; font-weight: 600; }
    table.tasks th.time, table.tasks td.time { width: 90px; text-align: center; }
    table.tasks th.chk, table.tasks td.chk { width: 64px; text-align: center; font-weight: 700; color: #237804; }
    .sign-box { width: 200px; height: 60px; border: 1px solid #ccc; border-radius: 2px; display: flex; align-items: center; justify-content: center; overflow: hidden; margin-top: 4px; }
    .sign-box img { max-width: 100%; max-height: 100%; }
    .muted { color: #999; }
    .signer { color: #666; margin-top: 4px; }
    .done { color: #237804; margin-top: 2px; }
</style>
</head>
<body>
    <div class="topbar"><span>OCM SECTION D - ANNEX 1</span><span class="brand">Fly Meta</span></div>
    <div class="title">Ramp Transit Checklist</div>
    <div class="station">Station: ${escapeHtml(flight.dep ?? "")}</div>

    <table class="head">
        <tbody>
            <tr>
                <td class="lbl">Flight Number</td>
                <td class="lbl">Aircraft REG</td>
                <td class="lbl">Date</td>
                <td class="lbl">STA/ETA</td>
                <td class="lbl">ATA</td>
            </tr>
            <tr>
                <td class="val">${escapeHtml(flight.flight_no ?? "")}</td>
                <td class="val">${escapeHtml(flight.aircraft_reg ?? "")}</td>
                <td class="val">${escapeHtml(flight.flight_date ?? "")}</td>
                <td class="val">${pair(flight.sta, flight.eta)}</td>
                <td class="val">${hm(ata)}</td>
            </tr>
            <tr>
                <td class="lbl">RCO</td>
                <td class="lbl">Parking Spot</td>
                <td class="lbl">Crew Figure</td>
                <td class="lbl">STD/ETD</td>
                <td class="lbl">ATD</td>
            </tr>
            <tr>
                <td class="val">${escapeHtml(header.rco ?? "")}</td>
                <td class="val">${escapeHtml(header.parking_spot ?? "")}</td>
                <td class="val">${escapeHtml(header.crew_figure ?? "")}</td>
                <td class="val">${pair(flight.std, flight.etd)}</td>
                <td class="val">${hm(atd)}</td>
            </tr>
        </tbody>
    </table>

    <table class="tasks">
        <thead>
            <tr>
                <th>Tasks</th>
                <th class="time">Time</th>
                <th class="chk">Check (√)</th>
            </tr>
        </thead>
        <tbody>
            ${rows}
            <tr>
                <td><strong>Remark:</strong> ${escapeHtml(header.remark ?? "")}</td>
                <td colspan="2">
                    <strong>Signature:</strong>
                    ${signatureBlock}
                    <div class="signer">${signer}</div>
                    ${completedLine}
                </td>
            </tr>
        </tbody>
    </table>
</body>
</html>`;

    return { html, title };
}

const waitForImages = (doc) =>
    Promise.all(
        Array.from(doc.images).map(
            (img) =>
                new Promise((resolve) => {
                    if (img.complete) {
                        resolve();
                        return;
                    }
                    img.onload = resolve;
                    img.onerror = resolve;
                }),
        ),
    );

async function renderHtmlToCanvas(html) {
    const iframe = document.createElement("iframe");
    iframe.style.cssText = `position:fixed;left:-10000px;top:0;width:${RENDER_WIDTH_PX}px;height:100px;border:0;`;
    document.body.appendChild(iframe);
    try {
        const doc = iframe.contentDocument;
        doc.open();
        doc.write(html);
        doc.close();
        await waitForImages(doc);
        // 等系统字体就绪再截图，避免中文用回退字形。
        if (doc.fonts?.ready) await doc.fonts.ready.catch(() => {});
        iframe.style.height = `${doc.body.scrollHeight + 20}px`;
        return await html2canvas(doc.body, { scale: 2, backgroundColor: "#ffffff", windowWidth: RENDER_WIDTH_PX, logging: false });
    } finally {
        iframe.remove();
    }
}

async function canvasToPdfBytes(canvas) {
    const pdf = await PDFDocument.create();
    const contentWidthPt = A4_WIDTH_PT - PAGE_MARGIN_PT * 2;
    const contentHeightPt = A4_HEIGHT_PT - PAGE_MARGIN_PT * 2;
    const pxPerPt = canvas.width / contentWidthPt;
    const pageSlicePx = Math.floor(contentHeightPt * pxPerPt);

    // 检查单设计上收进 A4 一页；备注 / 签名把内容撑长时按整页高度竖切分页。
    for (let offsetY = 0; offsetY < canvas.height; offsetY += pageSlicePx) {
        const sliceHeightPx = Math.min(pageSlicePx, canvas.height - offsetY);
        const slice = document.createElement("canvas");
        slice.width = canvas.width;
        slice.height = sliceHeightPx;
        slice.getContext("2d").drawImage(canvas, 0, offsetY, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);

        const png = await pdf.embedPng(slice.toDataURL("image/png"));
        const page = pdf.addPage([A4_WIDTH_PT, A4_HEIGHT_PT]);
        const drawHeightPt = sliceHeightPx / pxPerPt;
        page.drawImage(png, {
            x: PAGE_MARGIN_PT,
            y: A4_HEIGHT_PT - PAGE_MARGIN_PT - drawHeightPt,
            width: contentWidthPt,
            height: drawHeightPt,
        });
    }
    return pdf.save();
}

export async function downloadChecklistPdf({ checklist, tzId }) {
    const { html, title } = buildChecklistHtml({ checklist, tzId });
    const canvas = await renderHtmlToCanvas(html);
    const bytes = await canvasToPdfBytes(canvas);
    saveAs(new Blob([bytes], { type: "application/pdf" }), `${title}.pdf`);
}

export default downloadChecklistPdf;
